import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import ListFilters from '@/common/components/ListFilters'
import ListHeader from '@/common/components/ListHeader'
import Table from '@/common/components/Table'
import TextField from '@/common/components/TextField'
import useDebounce from '@/common/hooks/useDebounce'
import { useFilterParams } from '@/common/hooks/useFilterParams'
import { Mapper } from '@/common/services/mapper'
import { TicketData, TicketDataTableItem } from '@/common/types/support/ticket.type'
import { UserData } from '@/common/types/users'
import { defaultColumns } from '@/features/support/external/constants/columns.constant'
import { options as statusOptions } from '@/features/support/external/constants/statusOptions.constant'
import { useExternalSupportTickets } from '@/features/support/external/hooks/useExternalSupportTickets'
import { ExternalSupportTicketsTransformer } from '@/features/support/external/services/externalSupportTicketsTransformer'
import { useSupportReasons } from '@/features/support/hooks/useSupportReasons'
import { useUsers } from '@/features/users/hooks/useUsers'
import { SearchIcon, TicketIcon } from 'lucide-react'

const ExternalSupportList = () => {
  const { filterParams, setFilterParams } = useFilterParams()

  const [searchName, setSearchName] = useDebounce(filterParams.name || '')
  const [searchUser, setSearchUser] = useDebounce('');
  const [searchSupportReason, setSearchSupportReason] = useDebounce('');

  const withoutResponsible = filterParams.responsible_id?.value === 'no_attribution'

  const {
    data: supportTickets,
    meta,
    isLoading: supportTicketsIsLoading,
    error: supportTicketsError = {} as any,
    refetch: refetchSupportTickets,
  } = useExternalSupportTickets({
    filters: {
      page: filterParams.page || 1,
      search: searchName,
      status: filterParams.status?.value || '',
      reason_id: filterParams.reason_id?.value || '',
      responsible_id: !withoutResponsible ? filterParams.responsible_id?.value : '',
      responsible_id_nil: withoutResponsible || undefined
    },
    mapper: Mapper.mapWithTransformer<TicketData[], TicketDataTableItem[]>({
      transformer: ExternalSupportTicketsTransformer.tableTransformer(),
    }),
  })

  const { data: users } = useUsers({
    filters: { search: searchUser },
    mapper: Mapper.mapToOptions<UserData>({ labelFieldName: 'full_name', valueFieldName: 'id' })
  });

  const { data: supportReasonOptions } = useSupportReasons({
    filters: { search: searchSupportReason },
    mapper: Mapper.mapToOptions({
      labelFieldName: 'display_name',
      valueFieldName: 'id',
    }),
  })

  return (
    <div>
      <div className="flex flex-col gap-6">
        <ListHeader
          Icon={<TicketIcon className="size-10" strokeWidth={1.5} />}
          title="Chamados Externos"
          createButtonText="Abrir chamado"
          dataTestid="new_support_ticket_button"
          operationKlassToCheckPermission="Admin::Support::Tickets::Create"
        />

        <ErrorMessageWrapper
          className="h-[calc(100vh-128px)]"
          statusCode={supportTicketsError?.status}
          refetchFunction={refetchSupportTickets}
        >
          <div className="h-[calc(100vh-128px)] flex flex-col gap-6">
            <div className="flex flex-col gap-4">
              <TextField
                name="filter_search"
                LeftIcon={<SearchIcon />}
                placeholder="Pesquisar"
                className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
                value={filterParams.name || ''}
                onChange={(e: any) => {
                  setSearchName(e.target?.value)
                  setFilterParams((prev) => ({
                    ...prev,
                    name: e.target?.value,
                  }))
                }}
              />

              <ListFilters
                filters={[
                  {
                    className: 'min-w-[120px]',
                    onChange: (e: any) =>
                      setFilterParams((params) => ({
                        ...params,
                        status: e.value ? e : undefined,
                      })),
                    value: filterParams.status || undefined,
                    options: [{ label: 'Todos', value: '' }, ...(statusOptions || [])],
                    placeholder: 'Status',
                    dataTestid: 'filter_status',
                  },
                  {
                    className: 'min-w-[120px]',
                    onChange: (e: any) =>
                      setFilterParams((params) => ({
                        ...params,
                        reason_id: e.value ? e : undefined,
                      })),
                    onInputChange: setSearchSupportReason,
                    value: filterParams.reason_id || undefined,
                    options: [{ label: 'Todos', value: '' }, ...(supportReasonOptions || [])],
                    placeholder: 'Motivo',
                    dataTestid: 'filter_reason',
                  },
                  {
                    className: 'min-w-[120px]',
                    onChange: (e: any) =>
                      setFilterParams((params) => ({
                        ...params,
                        responsible_id: e.value ? e : undefined,
                      })),
                    onInputChange: setSearchUser,
                    value: filterParams.responsible_id || undefined,
                    options: [
                      { label: 'Todos', value: '' }, { label: 'Sem atribuição', value: 'no_attribution' },
                      ...(users || [])
                    ],
                    placeholder: 'Atribuído para',
                    dataTestid: 'filter_responsible_id',
                  },
                ]}
              />

            </div>

            <Table
              className="max-h-screen"
              columns={defaultColumns}
              isLoadingData={supportTicketsIsLoading}
              page={filterParams.page ? Number(filterParams.page) : 1}
              rowEmptyValue="--"
              rows={supportTickets}
              totalPages={meta.total_pages}
              totalCount={meta.total}
              hasPagination
              setPage={(p) => setFilterParams((params) => ({ ...params, page: p }))}
            />
          </div>
        </ErrorMessageWrapper>
      </div>
    </div>
  )
}

export default ExternalSupportList
